import { computed, Injectable, inject } from '@angular/core';
import { ReportsService } from './reports.service';
import { Session } from '../session/session.service';

@Injectable({ providedIn: 'root' })
export class DisplayService {
  private reports = inject(ReportsService);
  private session = inject(Session);

  showClientDropdown = computed(() => {
    const report = this.reports.currentReport();
    return report?.reportTemplate?.requiresClient ?? false;
  });

  reportUILoaded = computed(() => {
    const report = this.reports.currentReport();
    return !!report?.reportRequest?.controlsMap;
  });

  showSelectionCriteria = computed(() => {
    const report = this.reports.currentReport();
    return (
      !!report?.reportRequest?.controlsMap &&
      report?.reportTemplate?.requiresClient &&
      !!report?.reportInfo?.contractNumber &&
      report?.reportSections?.hasSelectionCriteria
    );
  });

  showReportFields = computed(() => {
    const report = this.reports.currentReport();
    return (
      !!report?.reportRequest?.controlsMap &&
      report?.reportTemplate?.requiresClient &&
      !!report?.reportInfo?.contractNumber &&
      (report?.reportSections?.hasUdf ||
        report?.reportSections?.hasDefaultFields)
    );
  });

  showSortOptions = computed(() => {
    const report = this.reports.currentReport();
    return (
      !!report?.reportRequest?.controlsMap &&
      report?.reportTemplate?.requiresClient &&
      !!report?.reportInfo?.contractNumber &&
      report?.reportSections?.hasSspb
    );
  });

  showFilterStep = computed(() => {
    const report = this.reports.currentReport();
    return (
      report?.reportTemplate?.requiresFilter ??
      report?.reportSections?.requiresFilter ??
      true
    );
  });

  showDocumentCriteria = computed(() => {
    const report = this.reports.currentReport();
    return report?.reportSections?.hasDocumentCriteria ?? false;
  });

  showPaycodeCriteria = computed(() => {
    const report = this.reports.currentReport();
    return report?.reportSections?.hasPaycodeCriteria ?? false;
  });

  showBankAccountCriteria = computed(() => {
    const report = this.reports.currentReport();
    return report?.reportSections?.hasBankCriteria ?? false;
  });

  showReserveCriteria = computed(() => {
    const report = this.reports.currentReport();
    return report?.reportSections?.hasReserveCriteria ?? false;
  });

  showFinancialCriteria = computed(() => {
    const report = this.reports.currentReport();
    const controlsMap = report?.reportRequest?.controlsMap;
    return (
      controlsMap?.has('FIN_CUSTOM_FIELD_1') ||
      controlsMap?.has('FIN_CUSTOM_FIELD_2') ||
      controlsMap?.has('FIN_CUSTOM_FIELD_3') ||
      controlsMap?.has('NON_FINANCIAL_FIELD') ||
      false
    );
  });

  showScheduleSetup = computed(() => {
    if (this.reports.isEditPersonalizedMode()) {
      return false;
    }
    if (this.reports.isSchedulePersonalizedMode()) {
      return true;
    }
    const report = this.reports.currentReport();
    if (report?.reportSections?.runOnceOnly) {
      return true;
    }
    return report?.reportSections?.hasScheduleSetup ?? false;
  });

  showRecurSchedOptions = computed(() => {
    if (this.reports.isSchedulePersonalizedMode()) {
      return true;
    }
    const report = this.reports.currentReport();
    return report?.reportSections?.hasRecurSchedOptions ?? false;
  });

  showEmailRecipient = computed(() => {
    const report = this.reports.currentReport();
    const hasReportCapability =
      report?.reportSections?.hasEmailRecipient ?? false;
    const hasTokenAccess = this.session.authorize('EmailReports');
    return hasReportCapability && hasTokenAccess;
  });

  showCustomEmail = computed(() => {
    const report = this.reports.currentReport();
    const hasReportCapability = report?.reportSections?.hasCustomEmail ?? false;
    const hasTokenAccess = this.session.authorize('EmailReports');
    return hasReportCapability && hasTokenAccess;
  });

  hasEmailAccess(): boolean {
    return this.session.authorize('EmailReports');
  }

  showDyoFinancialFields = computed(() => {
    const report = this.reports.currentReport();
    return report?.reportSections?.hasCustomFinancials ?? false;
  });

  showDyoNonFinancialFields = computed(() => {
    const report = this.reports.currentReport();
    return report?.reportSections?.hasCustomNonFinancials ?? false;
  });
}
